import { BEFORE_CONNECTED_CALLBACK, HOST_ELEMENT_KEY } from './renderer';

/**
 * The {@link Attribute} decorator can be used to bind the Component or Directive's
 * DOM element with a local property. This allows you to easily read or set the value
 * of the bound element's attribute.
 *
 * Say you're interested in data which has been placed as an attribute
 * (`interesting-data="5"`) on a Component's DOM element. We can easily get access
 * to this attribute by using the {@link Attribute} decorator in our component.
 *
 * ```typescript
 * @DomComponent({
 *   selector: 'aa-number',
 * })
 * class NumberComponent {
 *  // The property name will be used to retrieve the matching
 *  // attribute (interestingData becoming interesting-data).
 *  @Attribute()
 *  private readonly interestingData: string;
 *
 *  // You can also manually specify the name of the attribute by
 *  // providing it as an argument.
 *  @Attribute('interesting-data')
 *  private readonly otherWayOfGettingIt: string;
 * }
 * ```
 *
 * The {@link Attr} decorator works in two ways. It sets up a binding between
 * the property and the element's attribute, which therefore also allows you to
 * easily set an attribute on the Directive's or Component's DOM Element.
 *
 * In the following example we're adding the `novalidate` attribute
 * to each element which has the `aa-no-validate` directive connected to it.
 *
 * ```typescript
 * @DomDirective({
 *   selector: 'aa-no-validate',
 * })
 * export class Mask {
 *   @Attribute()
 *   readonly novalidate = 'novalidate';
 * }
 * ```
 *
 * A full overview of all available options:
 * ```typescript
 * @Attribute({
 *  // Name is automatically converted from camelCase to dash-seperated
 *  // When no name is provided it will use the property's name
 *  name: 'testCase',
 *
 *  // When set to true (false by default) it will return a parsed version
 *  // of the attribute's data. New data will be 'stringified' before setting.
 *  json: false,
 *
 *  // Indicates whether the incoming value should be parsed into a number
 *  number: false,
 * })
 * ```
 *
 * @param name
 */
export const Attribute = (nameOptions?: Partial<AttrOptions> | string): PropertyDecorator => {
  return function (target: any, propertyKey: string | symbol) {
    let inputOptions: AttrOptions;

    // Check the type of inputOptions and create an inputOptions object
    if (typeof (nameOptions) === 'string') {
      inputOptions = {
        name: nameOptions,
      };
    } else {
      inputOptions = {
        ...nameOptions,
        name: String((nameOptions === undefined || (nameOptions && !nameOptions.name)) ? propertyKey : nameOptions.name),
      };
    }

    // Normalize input options
    const options = normalizeOptions(inputOptions);

    // Caching property key
    const CACHING_PROP_KEY = `__${String(propertyKey)}JsonCache__`;

    // Execute everything during 'beforeConnected' lifecycle
    const beforeConnectedCallback = target[BEFORE_CONNECTED_CALLBACK] || function () { };
    target[BEFORE_CONNECTED_CALLBACK] = function () {
      // Grab "default value"
      const defaultValue = this[propertyKey];

      if (options.json) {
        // Create new 'caching' property. This property will be used to store the actual value
        // and can be used for caching purposes when using JSON parser.
        Object.defineProperty(this, CACHING_PROP_KEY, {
          writable: true,
          enumerable: false,
          value: { raw: null, parsed: null },
        });
      }

      // Replace property by a getter/setter which syncs with the element's attribute
      Object.defineProperty(this, propertyKey, {
        get: function () {
          const hostElm: HTMLElement = this[HOST_ELEMENT_KEY]();
          let value: string | null | number = hostElm.getAttribute(options.name);

          // Parse as JSON when enabled. Note that this uses the CACHING_PROP
          // to retrieve a parsed version when value didn't change
          if (options.json) {
            // If raw value equals caching property raw value we return the
            // cached parsed value
            if (this[CACHING_PROP_KEY].raw === value) {
              value = this[CACHING_PROP_KEY].parsed;
            } else {
              try {
                // Save raw, parse and save parsed
                this[CACHING_PROP_KEY].raw = value;
                value = JSON.parse(value || '');
                this[CACHING_PROP_KEY].parsed = value;
              } catch (e) {
                console.warn(`@Attribute(): Expected valid JSON, got invalid JSON: '${String(value).substring(0, 255)} ...'`);
              }
            }
          } else if (options.number && value) {
            value = parseFloat(value);
          }

          // Set default value on falsey values (except 0)
          if (!value && value !== 0) {
            value = defaultValue;
          }

          return value;
        },
        set: function (value) {
          const hostElm: HTMLElement = this[HOST_ELEMENT_KEY]();

          if (value !== undefined && value !== null) {
            // If JSON is enabled stringify input before saving
            if (options.json) {
              try {
                value = JSON.stringify(value);
              } catch (e) {
                console.warn(`@Attribute(): Expected valid JSON, got invalid JSON: '${String(value).substring(0, 255)} ...'`);
              }
            }

            // Only set when current value does not match new value
            const currentValue = hostElm.getAttribute(options.name);
            if (currentValue !== value) {
              hostElm.setAttribute(options.name, value);
            }
          } else {
            hostElm.removeAttribute(options.name);
          }
        },
        configurable: true,
      });

      // Force the execution of our newly defined 'setter' to make sure property
      // values are reflected into the DOM
      this[propertyKey] = this[propertyKey];

      // Continue with chain
      beforeConnectedCallback.call(this);
    };
  };
};

/**
 * @deprecated Version 0 used to have Attr() as the Attribute decorator. This doesn't work
 * because native JS already has an Attr interface, therefore we've renamed this to Attribute()
 *
 * @param args
 */
export const Attr = (...args: any[]) => {
  console.warn('[deprecated]: Attr() is decorated, please consider moving over to Attribute()');
  return Attribute(...args);
};

export interface AttrOptions {
  name: string;
  json?: boolean;
  number?: boolean;
}

/**
 * Creates an options object with default fallback valeus from the
 * provided options object or name (string).
 *
 * @param nameOptions options object or name in string form
 */
function normalizeOptions(nameOptions: AttrOptions): AttrOptions {
  // Replace camelCase to dash-seperated
  nameOptions.name = nameOptions.name.replace(/([a-zA-Z])(?=[A-Z])/g, '$1-').toLowerCase();

  return {
    json: false,
    ...nameOptions,
  };
}
